// The Create tab. The pipeline (classify → spec → generate → validate → repair) runs in this
// page: the prompts are bundled, the validator soaks the generated module with bots right here,
// and only the model calls leave the browser, proxied by the game server, which holds the
// OpenRouter key. A valid game previews live in the page; Publish asks the server to open a
// pull request on GitHub (CI's tests then gate the deploy), and Download saves game.js.

import { runPipeline, totals, type PipelineResult } from "../gen/pipeline.js";
import type { ChatResponse, Model } from "../gen/openrouter.js";
import { validateSource, type Finding } from "../gen/validate.js";
import type { GameModule } from "../contract.js";
import type { Spec } from "../gen/spec.js";
import { games } from "../../games/index.js";
import type { ServerAddress } from "./config.js";
import { browserSources } from "./gensources.js";
import { loadGeneratedModule } from "./genload.js";
import { mountPreviewModule, type PreviewHandle } from "./preview.js";
import { icon } from "./icons.js";

export interface CreateElements {
  prompt: HTMLTextAreaElement;
  build: HTMLButtonElement;
  status: HTMLElement;
  log: HTMLElement;
  result: HTMLElement;
  preview: HTMLCanvasElement;
  spec: HTMLElement;
  findings: HTMLElement;
  play: HTMLButtonElement;
  /** Development only: write the game into this checkout's feed. Hidden unless the server offers it. */
  add: HTMLButtonElement;
  publish: HTMLButtonElement;
  download: HTMLButtonElement;
  change: HTMLButtonElement;
  outcome: HTMLElement;
}

export interface CreateOptions {
  address: ServerAddress | null;
  /** The public demo: show the tab, say it works and how to get access, generate nothing. */
  locked?: boolean;
  els: CreateElements;
  /** The creator's display name, for the pull request. */
  author: () => string;
  toast: (msg: string) => void;
}

export interface CreateHandle {
  /** Pause the preview while another view is showing. */
  setActive(on: boolean): void;
  /** The game built in this page, loaded and ready for an in-page server, if there is one. */
  draft(): { module: GameModule; spec: Spec } | null;
}

/** The last build, kept in this browser so a reload does not throw away what it cost. */
export interface SavedDraft {
  prompt: string;
  spec: Spec;
  source: string;
  ok: boolean;
  findings: Finding[];
  /** The token bill line, as shown on the spec card. */
  bill: string;
  at: number;
  /** The slug it was added to the feed under, once it has been. */
  added?: string;
}

const DRAFT_KEY = "p2m.draft";

function readDraft(): SavedDraft | null {
  try {
    const raw = localStorage.getItem(DRAFT_KEY);
    const d = raw ? (JSON.parse(raw) as SavedDraft) : null;
    return d && typeof d.source === "string" && d.spec ? d : null;
  } catch {
    return null;
  }
}

function writeDraft(d: SavedDraft | null): void {
  try {
    if (d) localStorage.setItem(DRAFT_KEY, JSON.stringify(d));
    else localStorage.removeItem(DRAFT_KEY);
  } catch {
    /* no storage here: the draft lives until the page reloads */
  }
}

/** One line for the spec card: calls, tokens, thinking, cost, time. */
export function billLine(r: Pick<PipelineResult, "calls">): string {
  const t = totals(r.calls);
  return `${r.calls.length} model calls · ${t.prompt} tokens in (${t.cached} from cache) · ${t.completion} out${t.reasoning ? ` (${t.reasoning} thinking)` : ""}${t.cost ? ` · $${t.cost.toFixed(3)}` : ""} · ${(t.ms / 1000).toFixed(0)} s`;
}

export interface GenStatus {
  generation: boolean;
  publishing: boolean;
  /** The server can add a game straight to its checkout's feed (development only). */
  addToFeed?: boolean;
  models: { strong: string; fast: string } | null;
}

/** A Model that asks the game server, by role ("strong" or "fast"), never by model id. */
export function remoteModel(http: string, f: typeof fetch = fetch): Model {
  return async (req) => {
    const res = await f(`${http}/api/gen/chat`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ role: req.model, messages: req.messages, maxTokens: req.maxTokens, schema: req.schema, reasoning: req.reasoning }),
    });
    const text = await res.text();
    let data: { error?: string; code?: string } & Partial<ChatResponse>;
    try {
      data = JSON.parse(text);
    } catch {
      throw new Error(`the server answered ${res.status}: ${text.slice(0, 120)}`);
    }
    // The error keeps the server's code and usage, so the pipeline can retry a reply that ran
    // past the budget and still count what it cost.
    if (!res.ok) throw Object.assign(new Error(data.error ?? `the server answered ${res.status}`), { code: data.code, usage: data.usage });
    return data as ChatResponse;
  };
}

export async function fetchGenStatus(http: string, f: typeof fetch = fetch): Promise<GenStatus> {
  const res = await f(`${http}/api/gen/status`);
  if (!res.ok) throw new Error(`status ${res.status}`);
  return (await res.json()) as GenStatus;
}

/** A slug not already in the bundled catalogue (the server checks the live one again). */
export function freeSlug(slug: string, taken: (s: string) => boolean = (s) => s in games): string {
  if (!taken(slug)) return slug;
  for (let n = 2; n < 100; n++) if (!taken(`${slug}-${n}`)) return `${slug}-${n}`;
  return `${slug}-${Date.now().toString(36)}`;
}

/** Said on the locked Create tab of the public demo. Low key, and true. */
export const LOCKED_NOTE =
  "Locked in this demo. It does work: a model designs and writes the game from your description, and this page tests it with bots before you play it, in about two minutes. Each build costs real model time, which the demo isn't funded for yet. Contact Mat for access.";

export function mountCreate(o: CreateOptions): CreateHandle {
  const { els } = o;
  if (o.locked) {
    els.prompt.disabled = true;
    els.build.disabled = true;
    els.build.innerHTML = `${icon("lock")} Build`;
    els.status.classList.add("locked");
    els.status.innerHTML = `${icon("lock")} ${LOCKED_NOTE}`;
    return { setActive() {}, draft: () => null };
  }
  let status: GenStatus | null = null;
  let building = false;
  let active = false;
  let preview: PreviewHandle | null = null;
  let made: { spec: Spec; source: string; ok: boolean; module?: GameModule } | null = null;
  /** The build on show, as kept in storage. */
  let current: SavedDraft | null = null;

  const say = (line: string) => {
    els.log.hidden = false;
    els.log.textContent += line + "\n";
    els.log.scrollTop = els.log.scrollHeight;
  };

  let buildStarted = 0;
  let buildTimer: number | undefined;
  const setBuildable = () => {
    const can = !!o.address && !!status?.generation && !building;
    els.build.disabled = !can;
    // A model call is silent for a minute or more; the clock on the button shows the build is alive.
    clearInterval(buildTimer);
    if (building) {
      buildStarted = Date.now();
      const tick = () => {
        const s = Math.round((Date.now() - buildStarted) / 1000);
        els.build.textContent = `Building… ${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;
      };
      tick();
      buildTimer = window.setInterval(tick, 1000);
    } else els.build.textContent = "Build";
  };

  const refreshStatus = async () => {
    if (!o.address) {
      els.status.textContent = "No game server configured, so nothing can generate here.";
      setBuildable();
      return;
    }
    try {
      status = await fetchGenStatus(o.address.http);
      els.status.textContent = status.generation
        ? `${status.models?.strong ?? "?"} designs and writes; ${status.models?.fast ?? "?"} sorts. Publishing ${status.publishing ? "opens a pull request" : "is off here: Download instead"}.`
        : "Generation is off on this server: it has no OPENROUTER_API_KEY.";
    } catch (err) {
      status = null;
      els.status.textContent = `Could not reach the game server (${(err as Error).message}).`;
    }
    setBuildable();
  };

  const stopPreview = () => {
    preview?.stop();
    preview = null;
  };

  const showPreview = async (spec: Spec, source: string) => {
    stopPreview();
    try {
      const mod = await loadGeneratedModule(source);
      if (made) made.module = mod;
      preview = mountPreviewModule(els.preview, mod, spec.slug, { bots: Math.min(6, spec.players.max), labelTop: 6 });
      preview.setActive(active);
    } catch (err) {
      say(`preview: the module did not load (${(err as Error).message})`);
    }
  };

  const renderSpec = (spec: Spec, bill: string) => {
    const li = (s: string) => `<li>${escapeHtml(s)}</li>`;
    els.spec.innerHTML =
      `<h3>${escapeHtml(spec.title)}</h3>` +
      `<div class="meta">${escapeHtml(spec.kind)} · ${escapeHtml(spec.look.style)} · up to ${spec.players.max} players · ${spec.world.w}×${spec.world.h}</div>` +
      `<p>${escapeHtml(spec.blurb)}</p>` +
      `<p><b>Controls.</b> ${escapeHtml(spec.controls)}</p>` +
      `<ul>${spec.rules.map(li).join("")}</ul>` +
      `<div class="meta">${spec.entities.map((e) => escapeHtml(e.type + (e.sprite ? ` (${e.sprite})` : ""))).join(", ")}</div>` +
      `<div class="meta">${escapeHtml(bill)}</div>`;
  };

  const renderFindings = (findings: Finding[]) => {
    els.findings.innerHTML = findings.map((f) => `<li class="${f.level}">${escapeHtml(`${f.code}: ${f.message}`)}</li>`).join("");
  };

  /** Show a build: spec card, findings, the live preview, and the buttons it earns. */
  const showResult = async (d: SavedDraft) => {
    const { spec, source } = d;
    current = d;
    made = { spec, source, ok: d.ok };
    renderSpec(spec, d.bill);
    renderFindings(d.findings);
    els.result.hidden = true;
    await showPreview(spec, source);
    els.result.hidden = false;
    els.play.disabled = !made.module;
    els.play.textContent = "Play";
    els.publish.disabled = !d.ok || !status?.publishing;
    els.publish.textContent = "Publish";
    // Locally, Add to feed is the way to publish; a greyed-out Publish beside it only confuses.
    els.publish.hidden = !status?.publishing && !!status?.addToFeed;
    els.add.hidden = !status?.addToFeed;
    els.add.disabled = !d.ok || !!d.added;
    els.add.textContent = d.added ? "On the feed" : "Add to feed";
    els.add.title = !d.ok ? "Only a game that passed every check can join the feed" : "";
    els.publish.title = !d.ok ? "Only a game that passed every check can be published" : !status?.publishing ? "Publishing is not set up on this server" : "";
    if (d.added) {
      els.outcome.innerHTML = `"${escapeHtml(spec.title)}" is on this checkout's feed as games/${escapeHtml(d.added)}/. <a href="#game/${encodeURIComponent(d.added)}">Play it from the feed</a>. Commit games/${escapeHtml(d.added)}/ and games/index.ts to put it on the live demo.`;
      return;
    }
    els.outcome.textContent = d.ok
      ? `"${spec.title}" passed every check. Click the game or Play to play it yourself against bots on a server in this page; ${status?.addToFeed ? "add it to the feed" : "publish it to the feed"} if it is good enough, or download the file.`
      : `"${spec.title}" still fails a check after the repair rounds; the findings are listed. Download the file to fix it by hand, or change the prompt and build again.`;
  };

  const build = async () => {
    const prompt = els.prompt.value.trim();
    if (!prompt || building || !o.address) return;
    building = true;
    setBuildable();
    els.log.textContent = "";
    els.log.hidden = false;
    els.result.hidden = true;
    els.outcome.textContent = "";
    stopPreview();
    made = null;
    const model = remoteModel(o.address.http);
    try {
      const r = await runPipeline({
        prompt,
        model,
        modelId: "strong",
        fastModel: model,
        fastModelId: "fast",
        sources: browserSources,
        validate: async (source, spec) => {
          say("validate: loading the module and soaking it with 8 bots in this page…");
          await nextFrame();
          return validateSource(source, spec, loadGeneratedModule);
        },
        report: say,
      });
      if (r.refused) {
        say(`declined: ${r.refused}`);
        els.outcome.textContent = r.refused;
        return;
      }
      const saved: SavedDraft = {
        prompt,
        spec: r.spec!,
        source: r.source!,
        ok: r.ok,
        findings: r.rounds.at(-1)?.report.findings ?? [],
        bill: billLine(r),
        at: Date.now(),
      };
      writeDraft(saved);
      await showResult(saved);
      els.result.scrollIntoView({ behavior: "smooth", block: "start" });
    } catch (err) {
      say(`failed: ${(err as Error).message}`);
      els.outcome.textContent = `Building failed: ${(err as Error).message}`;
    } finally {
      building = false;
      setBuildable();
    }
  };

  const publish = async () => {
    if (!made || !made.ok || !o.address) return;
    els.publish.disabled = true;
    els.publish.textContent = "Publishing…";
    const slug = freeSlug(made.spec.slug);
    try {
      const res = await fetch(`${o.address.http}/api/gen/publish`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ slug, title: made.spec.title, blurb: made.spec.blurb, spec: { ...made.spec, slug }, source: made.source, author: o.author() || undefined }),
      });
      const data = (await res.json()) as { url?: string; error?: string };
      if (!res.ok || !data.url) throw new Error(data.error ?? `the server answered ${res.status}`);
      els.outcome.innerHTML = `Pull request opened: <a href="${escapeHtml(data.url)}" target="_blank" rel="noopener">${escapeHtml(data.url)}</a>. Once its tests pass and it is merged, "${escapeHtml(made.spec.title)}" joins the feed.`;
      o.toast("Pull request opened");
      els.publish.textContent = "Published";
    } catch (err) {
      els.outcome.textContent = `Publishing failed: ${(err as Error).message}`;
      els.publish.disabled = false;
      els.publish.textContent = "Publish";
    }
  };

  const addToFeed = async () => {
    if (!made?.ok || !current || current.added || !o.address) return;
    els.add.disabled = true;
    els.add.textContent = "Adding…";
    const slug = freeSlug(made.spec.slug);
    try {
      const res = await fetch(`${o.address.http}/api/gen/add`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ slug, title: made.spec.title, blurb: made.spec.blurb, spec: { ...made.spec, slug }, source: made.source, author: o.author() || undefined }),
      });
      const data = (await res.json()) as { slug?: string; error?: string; findings?: Finding[] };
      if (!res.ok || !data.slug) {
        if (data.findings) renderFindings(data.findings);
        throw new Error(data.error ?? `the server answered ${res.status}`);
      }
      current.added = data.slug;
      writeDraft(current);
      o.toast("Added to the feed");
      // The dev server restarts on the new catalogue and Vite reloads this page; until then:
      await showResult(current);
    } catch (err) {
      els.outcome.textContent = `Adding to the feed failed: ${(err as Error).message}`;
      els.add.disabled = false;
      els.add.textContent = "Add to feed";
    }
  };

  const play = () => {
    if (made?.module) location.hash = "#draft";
  };

  const download = () => {
    if (!made) return;
    const blob = new Blob([made.source], { type: "text/javascript" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${made.spec.slug}.game.js`;
    a.click();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  };

  const change = () => {
    stopPreview();
    made = null;
    current = null;
    writeDraft(null);
    els.result.hidden = true;
    els.log.hidden = true;
    els.outcome.textContent = "";
    els.prompt.focus();
    els.prompt.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  els.build.addEventListener("click", () => void build());
  els.play.addEventListener("click", play);
  els.add.addEventListener("click", () => void addToFeed());
  els.preview.addEventListener("click", play);
  els.publish.addEventListener("click", () => void publish());
  els.download.addEventListener("click", download);
  els.change.addEventListener("click", change);
  els.prompt.addEventListener("keydown", (e) => {
    if ((e.ctrlKey || e.metaKey) && e.key === "Enter") void build();
  });
  els.build.disabled = true;
  // Status first, so a restored draft knows whether Publish is on.
  void refreshStatus().then(async () => {
    const d = readDraft();
    if (!d || made || building) return;
    if (!els.prompt.value.trim()) els.prompt.value = d.prompt;
    els.log.hidden = false;
    els.log.textContent = `restored the last build from this browser (${new Date(d.at).toLocaleString()}); Change clears it\n`;
    await showResult(d);
  });

  return {
    setActive(on) {
      active = on;
      preview?.setActive(on);
      if (on && !status) void refreshStatus();
    },
    draft() {
      return made?.module ? { module: made.module, spec: made.spec } : null;
    },
  };
}

/**
 * Let the log line paint before the soak blocks the thread for a second or two. A timer, not
 * requestAnimationFrame: a tab that is not being drawn (background, or an embedded pane that is
 * hidden) never fires animation frames, and the build would wait on one for ever.
 */
function nextFrame(): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, 30));
}

function escapeHtml(s: string): string {
  return s.replace(/[&<>"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" })[c]!);
}
